import { PortalDialogProps } from "@app/services/modal.service";
import Modal from "../modal/modal.component";
import Button from "../button";
import { FormControl } from "../form-control";
import Input from "../input";
import { Form, Formik, FormikContextType } from "formik";
import { Images } from "@assets/images";
import { BookSegment } from "@app/types";
import { ChangeEvent, createRef, useEffect, useRef } from "react";
import { updateBookSegmentValidationSchema } from "@app/validations";
import { MAX_SIZE_UPLOAD, SystemMessage } from "@app/constants";
import TextArea from "../textarea";

interface BookSegmentModalProps {
  bookSegment: BookSegment;
}

function BookSegmentModal({
  portalDialogRef,
  portalData,
}: PortalDialogProps<BookSegmentModalProps>) {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const formRef = createRef<
    FormikContextType<{
      content: string;
      filename?: string;
      mediaLink?: string;
    }>
  >();

  const onSubmit = (values: { content: string; mediaLink?: string }) => {
    if (!formRef.current) return;

    const file = inputFileRef.current?.files?.[0] ?? null;

    if (file && file.size > MAX_SIZE_UPLOAD) {
      formRef.current.setFieldError("filename", SystemMessage.MAX_SIZE_UPLOAD);

      return;
    }

    const bookSegment = {
      content: values.content,
      mediaLink: values.mediaLink,
      media: file,
    };

    portalDialogRef.close({ bookSegment });
  };

  const onAddFileClick = () => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!formRef.current) return;

    if (!event.target.files?.length) {
      formRef.current.setFieldError("filename", SystemMessage.INPUT_REQUIRED);
      formRef.current.setFieldValue("filename", "");
      formRef.current.submitForm();

      return;
    }

    const file = event.target.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      formRef.current.setFieldError("filename", SystemMessage.MAX_SIZE_UPLOAD);
      formRef.current.setFieldValue("filename", "");
      formRef.current.submitForm();

      return;
    }

    formRef.current.setFieldError("filename", undefined);
    formRef.current.setFieldValue("filename", file.name);
  };

  useEffect(() => {
    if (portalData?.bookSegment && formRef.current) {
      formRef.current.setValues({
        content: portalData.bookSegment.content,
        mediaLink: portalData.bookSegment.mediaLink,
        filename: "",
      });
    }
  }, [portalData?.bookSegment]);

  return (
    <Modal onCancel={portalDialogRef.close}>
      <div className="w-[650px] py-7 px-6">
        <div className="text-black font-semibold text-xl">Chỉnh sửa</div>
        <Formik
          innerRef={formRef}
          initialValues={{ content: "", filename: "", mediaLink: "" }}
          onSubmit={onSubmit}
          validationSchema={updateBookSegmentValidationSchema}
          validateOnChange
          validateOnBlur
        >
          <Form>
            <FormControl name="content">
              <TextArea
                textAreaClassName="bg-color pl-4 border-none outline-none text-[#2C2D33] rounded-lg"
                className="mt-7"
                placeholder="Nội dung"
                width="100%"
                height="auto"
                rows={4}
              />
            </FormControl>
            <FormControl name="mediaLink">
              <Input className="mt-3" placeholder="Link media" />
            </FormControl>
            <FormControl name="filename">
              <Input
                className="mt-3"
                inputClassName="!pl-4 pr-[50px]"
                placeholder="File media"
                disabled
                inputIcon={Images.LinkIcon.default}
                inputIconClassName="!right-5 !top-[14px] !left-auto !w-6 !h-6 cursor-pointer hover:bg-gray-300"
                onIconClick={onAddFileClick}
              />
            </FormControl>
            <input
              ref={inputFileRef}
              type="file"
              name="file"
              hidden
              onChange={handleFileChange}
            />
            <div className="mt-11 flex justify-center gap-4">
              <Button
                size="xs"
                label="Hủy"
                theme="secondary"
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
                onClick={portalDialogRef.close}
              />
              <Button
                type="submit"
                size="xs"
                label="Lưu"
                width={180}
                className="!h-[51px] !leading-[51px]"
                labelClassName="text-xl"
              />
            </div>
          </Form>
        </Formik>
      </div>
    </Modal>
  );
}

export default BookSegmentModal;
